import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { useExcelData } from "../../context/ExcelDataContext"; // IMPORT CONTEXT
import { FiltersBar, type FiltersBarConfig } from "../components/dashboard/FiltersBar";
import { CostBreakdownStructureCard } from "../components/dashboard/CostBreakdownStructureCard";
import { SensitivityTornadoCard } from "../components/dashboard/SensitivityTornadoCard";
import { ScenarioAnalysisCard } from "../components/dashboard/ScenarioAnalysisCard";
import { FiveYearForecastCard } from "../components/dashboard/FiveYearForecastCard";
import { useCallback, useMemo, useState } from "react";

const normalizeLabel = (raw?: string) => {
  if (!raw) return "";
  return raw
    .replace(/[^\p{L}\p{N}\s&/-]/gu, "") // strip ₹, %, \, . etc.
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
};
const uniq = (arr: string[]) => Array.from(new Set(arr.filter(Boolean)));

const T = {
  purple: "#43224d",
  purpleLight: "#f5f0f7",
  purpleMid: "#3265AA",
  red: "#AD5E9D",
  gray: "#4c4c4d",
  grayMuted: "#9e97a3",
  chartA: "#3265AA",
  chartB: "#AD5E9D",
  chartD: "#93c5fd",
  chartE: "#f9a8d4",
  chartG: "#e2e8f0",
};

const fxData = [
  { name: "USD", hedged: 820, unhedged: 260 },
  { name: "EUR", hedged: 170, unhedged: 90 },
  { name: "SAR", hedged: 640, unhedged: 28 },
];

const optLevers = [
  { label: "Integrated Services", value: 34, color: '#AD5E9D' },
  { label: "Performance Contract", value: 26, color: '#5983BC' },
  { label: "NPT Reduction", value: 29, color: '#8F87BB' },
  { label: "Well Standardisation", value: 11, color: '#70C3EB' },
  { label: "Fluid Optimisation", value: 14, color: '#F5A0BD' },
  { label: "Digital Drilling", value: 7, color: '#594060' },
  { label: "RIG Scheduling AI", value: 9, color: '#5B84BB' },
  { label: "Variation Reduction", value: 8, color: '#AD5E9D' },
];

/* ================= HELPER FUNCTIONS ================= */

const toNumber = (value: any): number => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const sumByKey = (rows: any[] | undefined, key: string): number =>
  (rows ?? []).reduce((acc, row) => acc + toNumber(row?.[key]), 0);

const getFieldTotal = (item: any, field: string): number => {
  const answer = item?.answer ?? {};
  const totals = answer?.totals ?? item?.totals ?? {};

  // Prefer pre-computed totals when present.
  const totalValue = toNumber(totals?.[field]);
  if (totalValue > 0) return totalValue;

  // Fallback for payloads where only detailed rows are present.
  if (field === "material_cost") return sumByKey(answer?.material_cost, "cost");
  if (field === "labour_cost") {
    const salary = sumByKey(answer?.labour_cost, "salary");
    const benefits = sumByKey(answer?.labour_cost, "benefits");
    return salary + benefits;
  }
  if (field === "capital_cost") return sumByKey(answer?.capital_cost, "cost");
  if (field === "utility_cost") return sumByKey(answer?.utility_cost, "cost");

  return totalValue;
};

const sumField = (items: any[], field: string): number =>
  items.reduce((acc, item) => acc + getFieldTotal(item, field), 0);

const toPercent = (value: number, total: number): number =>
  Number(((value / (total || 1)) * 100).toFixed(2));

const getScopedItems = (
  analysisResult: Record<string, any> | null,
  selectedProduct: string = "All Products",
  selectedGeography: string = "All Geographies"
) => {
  const foreachItems: any[] = analysisResult?.data?.output?.foreach ?? [];
  return foreachItems.filter((item) => {
    const productMatch =
      selectedProduct === "All Products" ||
      normalizeLabel(item?.answer?.heading?.line_item_description) === selectedProduct;
    const geographyMatch =
      selectedGeography === "All Geographies" ||
      normalizeLabel(item?.answer?.heading?.supplier_region) === selectedGeography;
    return productMatch && geographyMatch;
  });
};

const getYAxisScale = (values: number[], minTicks: number = 5) => {
  const maxValue = Math.max(0, ...values);
  if (maxValue === 0) {
    return {
      max: 100,
      ticks: [0, 25, 50, 75, 100],
    };
  }

  const targetIntervals = Math.max(minTicks - 1, 1);
  const roughStep = maxValue / targetIntervals;
  const magnitude = Math.pow(10, Math.floor(Math.log10(roughStep)));
  const normalized = roughStep / magnitude;

  let step = 10 * magnitude;
  if (normalized <= 1) step = 1 * magnitude;
  else if (normalized <= 2) step = 2 * magnitude;
  else if (normalized <= 5) step = 5 * magnitude;

  const max = Math.ceil(maxValue / step) * step;
  const ticks = Array.from({ length: Math.floor(max / step) + 1 }, (_, i) => i * step);

  return { max, ticks };
};


const calculateCbsData = (
  analysisResult: Record<string, any> | null,
  selectedProduct: string = "All Products",
  selectedGeography: string = "All Geographies"
) => {
  const scopedItems = getScopedItems(analysisResult, selectedProduct, selectedGeography);

  if (scopedItems.length === 0) return [];

  const totals = {
    material_cost: sumField(scopedItems, "material_cost"),
    labour_cost: sumField(scopedItems, "labour_cost"),
    capital_cost: sumField(scopedItems, "capital_cost"),
    utility_cost: sumField(scopedItems, "utility_cost"),
    overheads: sumField(scopedItems, "overheads"),
    margins: sumField(scopedItems, "margins"),
    total_cost: sumField(scopedItems, "total_cost"),
  };

  const componentTotal =
    totals.material_cost +
    totals.labour_cost +
    totals.capital_cost +
    totals.utility_cost +
    totals.overheads +
    totals.margins;
  const totalCost = totals.total_cost || componentTotal || 1;

  const colors = [T.chartA, "#3265AA", T.red, "#93c5fd", "#a78bfa", "#f9a8d4", T.chartG];

  const dynamicCbsData = [
    {
      name: "Material Cost",
      value: toPercent(totals.material_cost, totalCost),
      color: colors[0],
    },
    {
      name: "Labour Cost",
      value: toPercent(totals.labour_cost, totalCost),
      color: colors[1],
    },
    {
      name: "Capital Cost",
      value: toPercent(totals.capital_cost, totalCost),
      color: colors[2],
    },
    {
      name: "Utility Cost",
      value: toPercent(totals.utility_cost, totalCost),
      color: colors[3],
    },
    {
      name: "Overheads",
      value: toPercent(totals.overheads, totalCost),
      color: colors[4],
    },
    {
      name: "Margins",
      value: toPercent(totals.margins, totalCost),
      color: colors[5],
    },
  ];

  return dynamicCbsData;
};

const calculateTornadoItems = (analysisResult: Record<string, any> | null, selectedProduct: string = "All Products", selectedGeography: string = "All Geographies") => {
  const scopedItems = getScopedItems(analysisResult, selectedProduct, selectedGeography);
  if (scopedItems.length === 0) return [];
  const costDrivers = [
    { label: "Material Cost", value: sumField(scopedItems, "material_cost") },
    { label: "Labour Cost", value: sumField(scopedItems, "labour_cost") },
    { label: "Capital Cost", value: sumField(scopedItems, "capital_cost") },
    { label: "Utility Cost", value: sumField(scopedItems, "utility_cost") },
  ];

  // neg = -10% scenario value, pos = +10% scenario value
  const dynamicTornadoItems = costDrivers.map((driver) => ({
    label: driver.label,
    neg: -parseFloat((driver.value * 0.9).toFixed(1)),
    pos: parseFloat((driver.value * 1.1).toFixed(1)),
  }));

  return dynamicTornadoItems;
};

const calculateKpiValues = (
  analysisResult: Record<string, any> | null,
  selectedProduct: string = "All Products",
  selectedGeography: string = "All Geographies"
) => {
  const scopedItems = getScopedItems(analysisResult, selectedProduct, selectedGeography);

  if (scopedItems.length === 0) {
    return {
      totalCost: "$0.0K",
      materialCost: "$0.0K",
      labourCost: "$0.0K",
      capitalCost: "$0.0K",
    };
  }

  const totals = {
    material_cost: sumField(scopedItems, "material_cost"),
    labour_cost: sumField(scopedItems, "labour_cost"),
    capital_cost: sumField(scopedItems, "capital_cost"),
    total_cost: sumField(scopedItems, "total_cost"),
  };

  return {
    totalCost: `$${totals.total_cost.toFixed(1)}K`,
    materialCost: `$${totals.material_cost.toFixed(1)}K`,
    labourCost: `$${totals.labour_cost.toFixed(1)}K`,
    capitalCost: `$${totals.capital_cost.toFixed(1)}K`,
  };
};

const calculateShouldCostGap = (
  analysisResult: Record<string, any> | null,
  selectedProduct: string = "All Products",
  selectedGeography: string = "All Geographies"
) => {
  const scopedItems = getScopedItems(analysisResult, selectedProduct, selectedGeography);

  const totalCost = sumField(scopedItems, "total_cost");
  const inflatedCost = totalCost * 1.2;
  const gap = inflatedCost - totalCost;
  const overpayPct = totalCost > 0 ? (gap / totalCost) * 100 : 0;

  return {
    value: `$${gap.toFixed(1)}M`,
    delta: `${overpayPct.toFixed(1)}% Overpay`,
    positive: gap >= 0,
  };
};

const truncateName = (name: string, max: number = 8): string =>
  name.length > max ? `${name.slice(0, max)}...` : name;

const calculateShouldCostBenchmarkData = (
  analysisResult: Record<string, any> | null,
  selectedProduct: string = "All Products",
  selectedGeography: string = "All Geographies"
) => {
  const scopedItems = getScopedItems(analysisResult, selectedProduct, selectedGeography);

  const totalsByProduct = scopedItems.reduce<Record<string, number>>((acc, item) => {
    const product = normalizeLabel(item?.answer?.heading?.line_item_description) || "Unknown";
    acc[product] = (acc[product] || 0) + getFieldTotal(item, "total_cost");
    return acc;
  }, {});

  return Object.entries(totalsByProduct)
    .map(([name, totalCost]) => ({
      name: truncateName(name),
      fullName: name,
      should: Number(totalCost.toFixed(1)),
      actual: Number((totalCost * 1.2).toFixed(1)),
    }))
    .sort((a, b) => b.should - a.should)
    .slice(0, 6);
};

const calculateRegionalCostTableData = (
  analysisResult: Record<string, any> | null,
  selectedProduct: string = "All Products",
  selectedGeography: string = "All Geographies"
) => {
  const scopedItems = getScopedItems(analysisResult, selectedProduct, selectedGeography);

  const totalsByRegion = scopedItems.reduce<
    Record<string, {
      region: string;
      materialCost: number;
      labourCost: number;
      utilityCost: number;
      capitalCost: number;
    }>
  >((acc, item) => {
    const region = normalizeLabel(item?.answer?.heading?.supplier_region) || "Unknown";

    if (!acc[region]) {
      acc[region] = {
        region,
        materialCost: 0,
        labourCost: 0,
        utilityCost: 0,
        capitalCost: 0,
      };
    }

    acc[region].materialCost += getFieldTotal(item, "material_cost");
    acc[region].labourCost += getFieldTotal(item, "labour_cost");
    acc[region].utilityCost += getFieldTotal(item, "utility_cost");
    acc[region].capitalCost += getFieldTotal(item, "capital_cost");
    return acc;
  }, {});

  return Object.values(totalsByRegion).sort((a, b) => a.region.localeCompare(b.region));
};

const calculateForecastData = (
  analysisResult: Record<string, any> | null,
  selectedProduct: string = "All Products",
  selectedGeography: string = "All Geographies"
) => {
  const scopedItems = getScopedItems(analysisResult, selectedProduct, selectedGeography);
  const baseCost = sumField(scopedItems, "total_cost");

  // Year-specific inflation rates (compounding)
  const yearInflation = [
    { label: "2025 E", rate: 1.039 },
    { label: "2026 F", rate: 1.04  },
    { label: "2027 F", rate: 1.042 },
    { label: "2028 F", rate: 1.048 },
    { label: "2029 F", rate: 1.044 },
    { label: "2030 F", rate: 1.041 },
  ];

  let prev = baseCost;
  return yearInflation.map(({ label, rate }, i) => {
    const base = Math.round(prev * rate);
    prev = base;
    // Savings accrue ~2.5% per year vs the inflated base
    const savingsFactor = i === 0 ? 1 : 1 - i * 0.026;
    const savings = Math.round(base * savingsFactor);
    return { year: label, base, savings };
  });
};

const calculateScenarioData = (analysisResult: Record<string, any> | null,selectedProduct: string = "All Products",
  selectedGeography: string = "All Geographies") => {
  const scopedItems = getScopedItems(analysisResult, selectedProduct, selectedGeography);
  const totalCost = sumField(scopedItems, "total_cost");
  const cagr = 1.07; // 7% CAGR
  const yearsToForecast = 2; // 2025 to 2027

  return [
    { name: "2025 Pessimistic", value: Math.round(totalCost * 1.1) },
    { name: "2025 Base", value: Math.round(totalCost) },
    { name: "2025 Optimistic", value: Math.round(totalCost * 0.95) },
    { name: "2027 Pessimistic", value: Math.round((totalCost * 1.1) * Math.pow(cagr, yearsToForecast)) },
    { name: "2027 Base", value: Math.round(totalCost * Math.pow(cagr, yearsToForecast)) },
    { name: "2027 Optimistic", value: Math.round((totalCost * 0.95) * Math.pow(cagr, yearsToForecast)) },
  ];
};

type KpiCardProps = {
  label: string;
  value: string;
  delta?: string;
  deltaLabel?: string;
  positive: boolean;
};
function NoRecordsFound({ message = "No Records Found" }: { message?: string }) {
  return (
    <div className="cma-no-records" role="status" aria-live="polite">
      <svg width="120" height="100" viewBox="0 0 224 209" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M194.912 51.0825V59.2599" stroke="#BABABA" stroke-linecap="round" stroke-linejoin="round"></path><path d="M190.832 55.1617H198.99" stroke="#BABABA" stroke-linecap="round" stroke-linejoin="round"></path><path d="M27.6445 19.5302V27.6886" stroke="#BABABA" stroke-linecap="round" stroke-linejoin="round"></path><path d="M23.5488 23.6094H31.7263" stroke="#BABABA" stroke-linecap="round" stroke-linejoin="round"></path><path d="M91.1289 4.21874C92.1558 4.21874 92.9883 3.38627 92.9883 2.35937C92.9883 1.33247 92.1558 0.5 91.1289 0.5C90.102 0.5 89.2695 1.33247 89.2695 2.35937C89.2695 3.38627 90.102 4.21874 91.1289 4.21874Z" fill="#CFCFCF"></path><path d="M150.744 78.1381C134.863 78.1381 118.698 77.493 103.577 73.2809C88.7397 69.1638 75.117 61.1761 62.9742 51.9362C55.0244 45.9217 47.7957 41.1404 37.4743 41.8614C27.3682 42.4147 17.7088 46.2066 9.92525 52.6761C-3.20417 64.06 -1.23096 85.3668 4.0246 100.337C11.9174 122.877 35.9374 138.492 56.3525 148.699C79.9362 160.482 105.853 167.312 131.828 171.258C154.596 174.711 183.852 177.235 203.584 162.36C221.704 148.699 226.674 117.507 222.235 96.4472C221.159 90.22 217.846 84.5999 212.919 80.6425C200.188 71.3457 181.196 77.5499 166.89 77.8724C161.578 77.9863 156.17 78.1191 150.744 78.1381Z" fill="#F2F2F2"></path><path d="M111.889 208.161C150.356 208.161 181.539 206.216 181.539 203.816C181.539 201.417 150.356 199.471 111.889 199.471C73.4218 199.471 42.2383 201.417 42.2383 203.816C42.2383 206.216 73.4218 208.161 111.889 208.161Z" fill="#F2F2F2"></path><path d="M141.469 6.8014L48.7368 19.3392C45.7669 19.7408 43.6849 22.4738 44.0864 25.4437L62.0695 158.45C62.4711 161.419 65.2041 163.501 68.174 163.1L160.906 150.562C163.876 150.16 165.958 147.427 165.556 144.458L147.573 11.4517C147.171 8.48188 144.438 6.39986 141.469 6.8014Z" fill="#D2D2D2"></path><path d="M172.279 26.3224H78.7037C75.7068 26.3224 73.2773 28.7518 73.2773 31.7487V165.965C73.2773 168.962 75.7068 171.391 78.7037 171.391H172.279C175.276 171.391 177.706 168.962 177.706 165.965V31.7487C177.706 28.7518 175.276 26.3224 172.279 26.3224Z" fill="white" stroke="#BABABA" stroke-linecap="round" stroke-linejoin="round"></path><path d="M147.972 130.523H102.986C101.299 130.523 99.9316 131.891 99.9316 133.578V133.597C99.9316 135.284 101.299 136.651 102.986 136.651H147.972C149.659 136.651 151.026 135.284 151.026 133.597V133.578C151.026 131.891 149.659 130.523 147.972 130.523Z" fill="#D2D2D2"></path><path d="M146.359 155.568H103.195C102.682 155.568 102.266 155.984 102.266 156.497V156.782C102.266 157.295 102.682 157.711 103.195 157.711H146.359C146.873 157.711 147.289 157.295 147.289 156.782V156.497C147.289 155.984 146.873 155.568 146.359 155.568Z" fill="#D2D2D2"></path><path d="M157.365 145.531H93.786C93.1049 145.531 92.5527 146.083 92.5527 146.764C92.5527 147.445 93.1049 147.997 93.786 147.997H157.365C158.046 147.997 158.598 147.445 158.598 146.764C158.598 146.083 158.046 145.531 157.365 145.531Z" fill="#D2D2D2"></path><path d="M168.995 30.743H81.9846C79.826 30.743 78.0762 32.4929 78.0762 34.6515V110.108C78.0762 112.266 79.826 114.016 81.9846 114.016H168.995C171.154 114.016 172.904 112.266 172.904 110.108V34.6515C172.904 32.4929 171.154 30.743 168.995 30.743Z" fill="white" stroke="#BABABA" stroke-linecap="round" stroke-linejoin="round"></path><path d="M137.103 90.6225C137.723 90.6077 138.323 90.4047 138.825 90.0405C139.327 89.6762 139.706 89.168 139.912 88.5832C140.118 87.9984 140.141 87.3648 139.978 86.7666C139.815 86.1684 139.474 85.6339 139 85.2341C135.043 81.9561 130.054 80.183 124.915 80.2281C119.777 80.2733 114.819 82.1338 110.92 85.4807C110.45 85.8791 110.113 86.4111 109.952 87.0058C109.792 87.6006 109.816 88.2302 110.022 88.8108C110.228 89.3914 110.606 89.8956 111.105 90.2565C111.604 90.6174 112.202 90.8178 112.817 90.8312L137.103 90.6225Z" fill="white" stroke="#BABABA" stroke-linecap="round" stroke-linejoin="round"></path><path d="M152.604 71.6304L141.543 65.2554L152.604 58.8615" stroke="#BABABA" stroke-width="0.71" stroke-linecap="round" stroke-linejoin="round"></path><path d="M97.4668 71.6304L108.528 65.2554L97.4668 58.8615" stroke="#BABABA" stroke-width="0.71" stroke-linecap="round" stroke-linejoin="round"></path><path d="M36.8086 112.1V120.277" stroke="#BABABA" stroke-linecap="round" stroke-linejoin="round"></path><path d="M32.7324 116.179H40.9099" stroke="#BABABA" stroke-linecap="round" stroke-linejoin="round"></path><path d="M191.461 117.887C192.488 117.887 193.32 117.054 193.32 116.028C193.32 115.001 192.488 114.168 191.461 114.168C190.434 114.168 189.602 115.001 189.602 116.028C189.602 117.054 190.434 117.887 191.461 117.887Z" fill="#CFCFCF"></path></svg>
      {message}
    </div>
  );
}
function KpiCard({ label, value, delta, deltaLabel, positive }: KpiCardProps) {
  return (
    <div className="cma-kpi-card">
      <div className="cma-kpi-label">{label}</div>
      <div className="cma-kpi-value">{value}</div>
      {delta && (
        <div className={`cma-kpi-delta ${positive ? "is-positive" : "is-negative"}`}>
          <span>{positive ? "▲" : "▼"}</span>
          <span>{delta}</span>
          {deltaLabel && <span className="cma-kpi-delta-label">{deltaLabel}</span>}
        </div>
      )}
    </div>
  );
}

type SectionCardProps = {
  title: string;
  subtitle?: string;
  badge?: string;
  badgeTone?: "purple" | "mid" | "red";
  children: React.ReactNode;
};

function SectionCard({ title, subtitle, badge, badgeTone = "purple", children }: SectionCardProps) {
  return (
    <div className="cma-card">
      <div className="cma-card-header">
        <div>
          <div className="cma-card-title">{title}</div>
          {subtitle && <div className="cma-card-subtitle">{subtitle}</div>}
        </div>
        {badge && <span className={`cma-card-badge cma-card-badge-${badgeTone}`}>{badge}</span>}
      </div>
      {children}
    </div>
  );
}

function Insight({ title, text }: { title: string; text: string }) {
  return (
    <div className="cma-insight-box">
      <div className="cma-insight-title">{title}</div>
      <div className="cma-insight-text">{text}</div>
    </div>
  );
}

export default function CostModelAnalysisView() {
  const { analysisResult } = useExcelData();
  const [selectedFilters, setSelectedFilters] = useState<Record<string, string>>({});
  const handleFiltersChange = useCallback((filters: Record<string, string>) => {
    setSelectedFilters(filters);
  }, []);

  const costModelFiltersConfig: FiltersBarConfig = useMemo(() => {
    const foreachItems: any[] = analysisResult?.data?.output?.foreach ?? [];

    const geographies = uniq(
      foreachItems.map((item) =>
        normalizeLabel(item?.answer?.heading?.supplier_region)
      )
    );

    const products = uniq(
      foreachItems.map((item) =>
        normalizeLabel(item?.answer?.heading?.line_item_description)
      )
    );


    return {
      title: "Filters",
      fields: [
        {
          key: "geography",
          label: "Geography",
          options: ["All Geographies", ...geographies],
        },
        {
          key: "category",
          label: "Category",
          options: ["Automotive"],
        },
        {
          key: "product",
          label: "Product",
          options: ["All Products", ...products],
        },
        {
          key: "timePeriod",
          label: "Time Period",
          options: [
            "FY25",
            "Q1 FY26",
            "Q2 FY26",
            "Q3 FY26",
            "Q4 FY26",
            "FY26 YTD",
          ], // unchanged
        },
      ],
      savedViews: ["Global FY26", "MENA Drill Focus", "Completion Deep Dive"],
    };
  }, [analysisResult]);

  // Calculate dynamic data based on analysis result + selected filters
  const selectedProduct = selectedFilters.product || "All Products";
  const selectedGeography = selectedFilters.geography || "All Geographies";
  const dynamicCbsData = calculateCbsData(analysisResult, selectedProduct, selectedGeography);
  const dynamicTornadoItems = calculateTornadoItems(analysisResult, selectedProduct, selectedGeography);
  const kpiValues = calculateKpiValues(analysisResult, selectedProduct, selectedGeography);
  const shouldCostGap = calculateShouldCostGap(analysisResult, selectedProduct, selectedGeography);
  const shouldCostBenchmarkData = calculateShouldCostBenchmarkData(analysisResult, selectedProduct, selectedGeography);
  const regionalCostTableData = calculateRegionalCostTableData(analysisResult, selectedProduct, selectedGeography);
  const scenarioData = calculateScenarioData(analysisResult, selectedProduct, selectedGeography);
  const forecastData = calculateForecastData(analysisResult, selectedProduct, selectedGeography);
  const shouldCostMax = shouldCostBenchmarkData.length > 0
    ? Math.max(...shouldCostBenchmarkData.flatMap((d) => [d.should, d.actual]))
    : 0;
  return (
    <div className="cma-view">
        {/* Breadcrumb */}
        <nav className="breadcrumb" aria-label="Breadcrumb">
          <span className="breadcrumb__current">Cost Model Analysis</span>
        </nav>

        {/* Page header */}
        <div className="page-header flex justify-between" style={{ marginBottom: 28 }}>
          <div className="page-header__left">
            <h2 className="page-header__title">Cost Model Analysis</h2>
            <p className="page-header__description">
              Well Construction Services · CBS, Should-Cost, Sensitivity & Forecasting
            </p>
          </div>
        </div>
      <div className="cma-content">
        
        <FiltersBar
          config={costModelFiltersConfig}
          onFiltersChange={handleFiltersChange}
        />

        <div className="cma-kpi-grid">
          <KpiCard label="Total Cost Base" value={kpiValues.totalCost} delta="7.5% YoY" positive={true} />
          <KpiCard label="Should-Cost Gap" value={shouldCostGap.value} delta={shouldCostGap.delta} positive={shouldCostGap.positive}/>
          <KpiCard label="Inflation Adj. 2025" value="+4.2%" delta="Energy: +6.8%" positive={true} />
          <KpiCard label="FX Exposure" value="$284M" delta="→ EUR/USD" positive={false} />
          <KpiCard label="Savings Delivered YTD" value="$46M" delta="51% of Target" positive={false} />
        </div>

        <div className="cma-two-col">
          <CostBreakdownStructureCard
            data={dynamicCbsData}
            insightTitle="Cost Driver Analysis"
            insightText="Rig day rates at 38% of total cost are the dominant driver. In tight market conditions, competitive bidding for rig capacity must occur 12-18 months ahead. Performance contracts linking day rates to drilling KPIs are essential."
            emptyState={<NoRecordsFound />}
          />

          <SectionCard
            title="Should-Cost vs Actual Benchmark"
            subtitle="Benchmark gap analysis by sub-commodity ($M)"
            badge="SHOULD-COST"
            badgeTone="mid"
          >
            {shouldCostBenchmarkData.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={shouldCostBenchmarkData} barGap={0} barCategoryGap="30%">
                    <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#888888" }} />
                    <YAxis
                      domain={[0, Math.ceil(shouldCostMax * 1.1)]}
                      tick={{ fontSize: 11, fill: "#888888" }}
                    />
                    <Tooltip
                      cursor={false}
                      contentStyle={{ fontSize: 12, borderRadius: 6 }}
                      formatter={(value: number, name: string) => [`$${value.toFixed(1)}K`, name]}
                      labelFormatter={(label: string, payload: any[]) =>
                        payload?.[0]?.payload?.fullName ?? label
                      }
                    />
                    <Bar dataKey="should" name="Should Cost ($K)" fill={T.chartA} radius={[5, 5, 0, 0]} barSize={46} activeBar={false} />
                    <Bar dataKey="actual" name="Actual Cost ($K)" fill={T.chartB} radius={[5, 5, 0, 0]} barSize={46} activeBar={false} />
                  </BarChart>
                </ResponsiveContainer>
                <div className="chart-legend sidebar-user--collapsed" aria-label="Should cost legend">
                  <span className="chart-legend__item">
                    <span className="chart-legend__dot" style={{ background: T.chartA }} />
                    Should Cost
                  </span>
                  <span className="chart-legend__item">
                    <span className="chart-legend__dot" style={{ background: T.chartB }} />
                    Actual Cost
                  </span>
                </div>
                <Insight
                  title="Benchmark Gap"
                  text="Actual cost is modeled as a 20% uplift over the filtered total cost. Should Cost is the filtered total cost aggregated per product."
                />
              </>
            ) : (
              <NoRecordsFound />
            )}
          </SectionCard>
        </div>

        <div className="cma-two-col">
          <SensitivityTornadoCard
            items={dynamicTornadoItems}
            insightTitle="Key Risk Drivers"
            insightText="Rig day rates are the highest sensitivity driver ($86M swing). Performance-linked contracts are critical to protect against day rate escalation. NPT reduction is the second highest lever - every 1% NPT reduction saves $12M."
            emptyState={<NoRecordsFound />}
          />
          <ScenarioAnalysisCard
            data={scenarioData}
            colorA={T.red}
            colorB={T.chartA}
            colorC={T.chartB}
            tickColor={T.gray}
            emptyState={<NoRecordsFound />}
          />
        </div>

        <div className="cma-two-col">
          <FiveYearForecastCard
            data={forecastData}
            baseStroke={T.chartA}
            savingsStroke={T.red}
            tickColor={T.gray}
            mutedTickColor={T.grayMuted}
            emptyState={<NoRecordsFound />}
          />

          <SectionCard
            title="FX Exposure & Currency Risk"
            subtitle="Spend by currency - hedged vs unhedged ($M)"
            badge="FX RISK"
            badgeTone="purple"
          >
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={fxData} barGap={0} barCategoryGap="35%">
                <XAxis dataKey="name" tick={{ fontSize: 12, fill: T.gray }} />
                <YAxis tick={{ fontSize: 11, fill: T.grayMuted }} />
                <Tooltip cursor={false} contentStyle={{ fontSize: 12, borderRadius: 6 }} />
                <Bar dataKey="hedged" name="Hedged ($M)" fill={T.chartA} radius={[6, 6, 0, 0]} barSize={46} />
                <Bar dataKey="unhedged" name="Unhedged ($M)" fill={T.chartE} radius={[6, 6, 0, 0]} barSize={46} />
              </BarChart>
            </ResponsiveContainer>
            <Insight
              title="FX Strategy"
              text="Well services are predominantly USD-denominated (Big 3 contractors). EUR exposure relates to European equipment and technology providers. SAR contracts for MENA national contractors carry low FX risk."
            />
          </SectionCard>
        </div>

        <div className="cma-two-col">
          <SectionCard
            title="Regional Cost Index Benchmarking"
            subtitle="Cost component totals by supplier region ($)"
            badge="BENCHMARK"
            badgeTone="purple"
          >
            {regionalCostTableData.length > 0 ? (
              <>
                <table className="cma-table">
                  <thead>
                    <tr>
                      {[
                        "Region",
                        "Material Cost ($)",
                        "Labour Cost ($)",
                        "Utility Cost ($)",
                        "Capital Cost ($)",
                      ].map((h) => (
                        <th key={h} className="cma-th">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {regionalCostTableData.map((row, i) => (
                      <tr key={row.region} className={i % 2 === 0 ? "cma-row-even" : "cma-row-odd"}>
                        <td className="cma-td">{row.region}</td>
                        <td className="cma-td">{row.materialCost.toLocaleString()}</td>
                        <td className="cma-td">{row.labourCost.toLocaleString()}</td>
                        <td className="cma-td">{row.utilityCost.toLocaleString()}</td>
                        <td className="cma-td">{row.capitalCost.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <Insight
                  title="Regional Spend Mix"
                  text="Regional values are aggregated from the filtered product and geography selection using supplier region as the grouping key."
                />
              </>
            ) : (
              <NoRecordsFound />
            )}
          </SectionCard>

          <SectionCard
            title="Optimisation Levers - Quantified Savings"
            subtitle="Ranked savings opportunities with implementation effort"
            badge="SAVINGS"
            badgeTone="mid"
          >
            <ResponsiveContainer width="100%" height={380}>
              <BarChart data={optLevers} layout="vertical" margin={{ top: 6, right: 8, left: 24, bottom: 2 }}>
                <XAxis
                  type="number"
                  domain={[0, 35]}
                  ticks={[0, 5, 10, 15, 20, 25, 30, 35]}
                  tick={{ fontSize: 11, fill: "#888888" }}
                />
                <YAxis
                  type="category"
                  dataKey="label"
                  width={150}
                  tick={{ fontSize: 11, fill: "#4c4c4d" }}
                />
                <Tooltip cursor={false} contentStyle={{ fontSize: 12, borderRadius: 6 }} />
                <Bar dataKey="value" radius={[4, 4, 4, 4]} barSize={24} activeBar={false}>
                  {optLevers.map((o) => (
                    <Cell key={o.label} fill={o.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </SectionCard>
        </div>
      </div>
    </div>
  );
}
